import { EnvironmentConfig, EnvironmentValidationError, REQUIRED_ENV_VARS } from '../types/environment';
import { devLog } from '../utils/devLogger';

/**
 * 從環境變數讀取配置值，如果不存在則使用備用值
 */
function getEnvVar(key: string, fallback?: string): string {
  const value = process.env[key];
  if (value !== undefined) {
    return value;
  }
  if (fallback !== undefined) {
    return fallback;
  }
  throw new EnvironmentValidationError(`必要環境變數 ${key} 未設定`);
}

/**
 * 驗證 URL 格式
 */
function validateUrl(url: string, varName: string): void {
  try {
    new URL(url);
  } catch {
    throw new EnvironmentValidationError(`環境變數 ${varName} 不是有效的 URL: ${url}`);
  }
}

/**
 * 驗證 LINE 配置完整性
 */
function validateLineConfig(lineConfig: {
  lineChannelId: string;
  lineRedirectUri: string;
  lineChannelSecret: string;
  lineLoginUri: string;
  lineLoginClientId: string;
  lineRedirectUrl: string;
}): void {
  const { lineChannelId, lineChannelSecret, lineLoginClientId } = lineConfig;
  
  // 驗證 LINE Channel ID 格式 (應該是數字)
  if (!/^\d+$/.test(lineChannelId)) {
    throw new EnvironmentValidationError(
      `LINE_CHANNEL_ID 應該是數字格式，目前值: ${lineChannelId}`
    );
  }
  
  // 驗證 LINE Channel Secret 長度 (通常是 32 字元)
  if (lineChannelSecret.length < 20) {
    throw new EnvironmentValidationError(
      `LINE_CHANNEL_SECRET 長度不足，可能不是有效的 secret`
    );
  }
  
  // 驗證 LINE Login Client ID 格式 (應該是數字)
  if (!/^\d+$/.test(lineLoginClientId)) {
    throw new EnvironmentValidationError(
      `LINE_LOGIN_CLIENT_ID 應該是數字格式，目前值: ${lineLoginClientId}`
    );
  }
}

/**
 * 驗證必要環境變數是否存在
 */
function validateRequiredEnvVars(): string[] {
  const missing: string[] = [];
  
  for (const envVar of REQUIRED_ENV_VARS) {
    if (!process.env[envVar]) {
      missing.push(envVar);
    }
  }
  
  return missing;
}

/**
 * 載入並驗證環境配置
 */
function loadEnvironmentConfig(): EnvironmentConfig {
  // 檢查必要環境變數
  const missingVars = validateRequiredEnvVars();
  if (missingVars.length > 0) {
    throw new EnvironmentValidationError(
      `缺少必要環境變數: ${missingVars.join(', ')}`,
      missingVars
    );
  }

  // 讀取環境設定
  const environment = getEnvVar('REACT_APP_ENVIRONMENT', 'development') as 'development' | 'production';
  
  // 驗證環境值
  if (environment !== 'development' && environment !== 'production') {
    throw new EnvironmentValidationError(
      `REACT_APP_ENVIRONMENT 必須是 'development' 或 'production'，目前值: ${environment}`
    );
  }

  // 讀取 API 配置
  const apiUrl = getEnvVar('REACT_APP_API_URL');
  validateUrl(apiUrl, 'REACT_APP_API_URL');

  // 讀取 LINE 配置
  const lineChannelId = getEnvVar('REACT_APP_LINE_CHANNEL_ID');
  const lineRedirectUri = getEnvVar('REACT_APP_LINE_REDIRECT_URI');
  const lineChannelSecret = getEnvVar('REACT_APP_LINE_CHANNEL_SECRET');
  const lineLoginUri = getEnvVar('REACT_APP_LINE_LOGIN_URI');
  const lineLoginClientId = getEnvVar('REACT_APP_LINE_LOGIN_CLIENT_ID');
  const lineRedirectUrl = getEnvVar('REACT_APP_LINE_REDIRECT_URL');

  // 驗證 LINE 配置完整性
  validateLineConfig({
    lineChannelId,
    lineRedirectUri,
    lineChannelSecret,
    lineLoginUri,
    lineLoginClientId,
    lineRedirectUrl
  });

  // 驗證 LINE URL
  validateUrl(lineRedirectUri, 'REACT_APP_LINE_REDIRECT_URI');
  validateUrl(lineLoginUri, 'LINE_LOGIN_URI');
  validateUrl(lineRedirectUrl, 'LINE_REDIRECT_URL');

  // 讀取 Google Maps 配置
  const googleMapsApiKey = getEnvVar('REACT_APP_GOOGLE_MAPS_API_KEY');
  const liffId = getEnvVar('REACT_APP_LIFF_ID');

  return {
    // 環境設定
    environment,
    isProduction: environment === 'production',
    isDevelopment: environment === 'development',

    // API 配置
    apiUrl,

    // LINE 配置
    lineChannelId,
    lineRedirectUri,
    lineChannelSecret,
    lineLoginUri,
    lineLoginClientId,
    lineRedirectUrl,

    // Google Maps 配置
    googleMapsApiKey,
    liffId
  };
}

/**
 * 全域環境配置實例
 * 在應用程式啟動時載入一次
 */
let environmentConfig: EnvironmentConfig | null = null;

/**
 * 記錄環境配置資訊
 */
function logEnvironmentConfig(config: EnvironmentConfig): void {
  const isDev = config.isDevelopment;
  
  if (isDev) {
    console.group('🚀 環境配置已載入');
    devLog(`📍 環境: ${config.environment}`);
    devLog(`📡 API URL: ${config.apiUrl}`);
    devLog(`📱 LINE Channel ID: ${config.lineChannelId}`);
    devLog(`📱 LINE Channel Secret: ${config.lineChannelSecret}`);
    devLog(`🔗 LINE Redirect URI: ${config.lineRedirectUri}`);
    devLog(`🗺️ Google Maps API Key: ${config.googleMapsApiKey.substring(0, 10)}...`);
    devLog(`📲 LIFF ID: ${config.liffId}`);
    console.groupEnd();
  } else {
    devLog(`🚀 ${config.environment} 環境已啟動`);
    devLog(`📡 API: ${config.apiUrl}`);
  }
  
  // 在開發模式下將配置加入到 window 物件供除錯使用
  if (isDev && typeof window !== 'undefined') {
    (window as any).__ENV_CONFIG__ = {
      environment: config.environment,
      apiUrl: config.apiUrl,
      lineChannelId: config.lineChannelId,
      // 不暴露敏感資訊
      hasLineSecret: !!config.lineChannelSecret,
      hasGoogleMapsKey: !!config.googleMapsApiKey
    };
  }
}

/**
 * 取得環境配置
 * 如果尚未載入，會自動載入配置
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  if (!environmentConfig) {
    environmentConfig = loadEnvironmentConfig();
    logEnvironmentConfig(environmentConfig);
  }
  return environmentConfig;
}

/**
 * 重新載入環境配置
 * 主要用於測試
 */
export function reloadEnvironmentConfig(): EnvironmentConfig {
  environmentConfig = loadEnvironmentConfig();
  return environmentConfig;
}
